import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import { HoloBuilderColors } from "@faro-lotv/ielement-types";
import { AnnotationColor, DARK_ANNOTATION_COLOR } from "./annotation-colors";
import { type JSX } from "react";
import { Vector3Tuple } from "three";
import { isPlainObject, omit } from "es-toolkit";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { PointAnnotationData } from "../annotation-mutation-utils";

/** Shapes the annotation tool can draw */
export enum AnnotationShape {
  point = "point",
  rectangle = "rectangle",
  polygon = "polygon",
}

/** How the outline of a shape annotation is drawn */
export enum OutlineStyle {
  solid = "solid",
  dashed = "dashed",
  cloud = "cloud",
}

export type AnnotationShapeStyle = {
  color: AnnotationColor;
  outline: OutlineStyle;
};

/** Where shapes can be drawn: on the 2D overview plane or on the image of a 360 */
export type ShapeContext = "sheet" | "pano";

/** A shape being drawn, in world coordinates */
export type ShapeDraft = Pick<PointAnnotationData, "parentId" | "type"> & {
  shape: AnnotationShape;
  points: Vector3Tuple[];
  isClosed: boolean;
};

type AnnotationShapesState = {
  /** Shape selected in the tool, kept as the last used one */
  shape: AnnotationShape;

  /** Where shapes can be drawn right now, undefined when only points are possible */
  context?: ShapeContext;

  draft?: ShapeDraft;

  /** Last hovered point while drawing, used for the rubber band segment */
  hoverPoint?: Vector3Tuple;

  /** Style used for the next shape, kept as the last used one */
  style: AnnotationShapeStyle;

  /** Mock of the backend: style of each shape annotation, by markup id */
  styles: Record<GUID, AnnotationShapeStyle | undefined>;

  /** Content of the right side panel, where annotations are created instead of a dialog over the canvas */
  panelContent?: JSX.Element;

  /** Title of the panel, or of the sheet on phones */
  panelTitle: string;

  /** What the style of the annotation in the panel applies to, set from the toolbar, undefined if it has none */
  panelStyleKind?: StyleKind;

  /** Color used for the next point annotation, kept as the last used one. Undefined for the default pin */
  pointColor?: AnnotationColor;

  /** On phones, true when the creation sheet is minimized to a bar, to look at the canvas */
  isSheetMinimized: boolean;

  /** Annotation whose style is being edited, previewed live until the edit is saved or cancelled */
  editedShape?: EditedStyle;

  /** True while the pointer is close enough to the first corner to close the polygon */
  canClosePolygon: boolean;

  /** True after the user discarded an annotation and the tool is still on, until the next one is started */
  wasDiscarded: boolean;

  setShape(shape: AnnotationShape): void;
  setContext(context?: ShapeContext): void;
  addDraftPoint(
    point: Vector3Tuple,
    target: Pick<ShapeDraft, "parentId" | "type">,
  ): void;
  undoDraftPoint(): void;
  closeDraft(): void;
  clearDraft(): void;
  setHoverPoint(point?: Vector3Tuple): void;
  setStyle(style: Partial<AnnotationShapeStyle>): void;
  /** Saves the style used for the new annotation of the given kind */
  saveStyle(markupId: GUID, kind: StyleKind): void;
  /** Sets the color of the next point, the dark one being the default pin */
  setPointColor(color: AnnotationColor): void;
  setPanelContent(content?: JSX.Element, info?: PanelInfo): void;
  startEditingShape(markupId: GUID, kind: StyleKind): void;
  setEditedStyle(style: Partial<AnnotationShapeStyle>): void;
  /** Ends the edit of a shape, keeping its new style only if saved */
  finishEditingShape(isSaved: boolean): void;
  setCanClosePolygon(canClose: boolean): void;
  setWasDiscarded(wasDiscarded: boolean): void;
};

export const DEFAULT_SHAPE_STYLE: AnnotationShapeStyle = {
  // Shapes need to stand out on the black lines of sheets and on dark 360s, so they are not dark like the pin
  color: HoloBuilderColors.Orange,
  outline: OutlineStyle.solid,
};

const NEW_ANNOTATION_TITLE = "New annotation";

/** What the panel shows, besides its content */
export type PanelInfo = { title: string; styleKind?: StyleKind };

/** What a style applies to: points only have a color, shapes also a line style */
export type StyleKind = "point" | "shape";

/** Style being edited. Points can have no color yet, they keep the default pin until one is picked */
export type EditedStyle = {
  markupId: GUID;
  kind: StyleKind;
  color?: AnnotationColor;
  outline: OutlineStyle;
};

/** Minimum number of corners of a closed polygon */
export const MIN_POLYGON_POINTS = 3;

export const useAnnotationShapesStore = create<AnnotationShapesState>()(
  persist(
    (set, get) => ({
      shape: AnnotationShape.point,
      style: DEFAULT_SHAPE_STYLE,
      styles: {},
      canClosePolygon: false,
      panelTitle: NEW_ANNOTATION_TITLE,
      isSheetMinimized: false,
      wasDiscarded: false,

      setShape: (shape) => set({ shape, draft: undefined }),
      setContext: (context) => set({ context }),
      addDraftPoint: (point, target) => {
        const { draft, shape } = get();
        const points = draft && !draft.isClosed ? draft.points : [];
        set({
          wasDiscarded: false,
          draft: {
            ...target,
            shape,
            points: [...points, point],
            isClosed: false,
          },
        });
      },
      undoDraftPoint: () => {
        const { draft } = get();
        if (!draft || draft.isClosed) return;
        const points = draft.points.slice(0, -1);
        set({ draft: points.length ? { ...draft, points } : undefined });
      },
      closeDraft: () => {
        const { draft } = get();
        if (!draft || draft.points.length < MIN_POLYGON_POINTS) return;
        set({
          draft: { ...draft, isClosed: true },
          hoverPoint: undefined,
          canClosePolygon: false,
        });
      },
      clearDraft: () =>
        set({
          draft: undefined,
          hoverPoint: undefined,
          canClosePolygon: false,
        }),
      setHoverPoint: (hoverPoint) => set({ hoverPoint }),
      setStyle: (style) => set({ style: { ...get().style, ...style } }),
      saveStyle: (markupId, kind) => {
        const { style, pointColor, styles } = get();
        const saved =
          kind === "shape"
            ? style
            : pointColor && { ...DEFAULT_SHAPE_STYLE, color: pointColor };
        if (saved) set({ styles: { ...styles, [markupId]: saved } });
      },
      setPointColor: (color) => set({ pointColor: pointColorOf(color) }),
      setPanelContent: (panelContent, info) =>
        set({
          panelContent,
          panelTitle: info?.title ?? NEW_ANNOTATION_TITLE,
          panelStyleKind: info?.styleKind,
        }),
      startEditingShape: (markupId, kind) => {
        const saved = get().styles[markupId];
        set({
          editedShape: {
            markupId,
            kind,
            color:
              saved?.color ??
              (kind === "shape" ? DEFAULT_SHAPE_STYLE.color : undefined),
            outline: saved?.outline ?? DEFAULT_SHAPE_STYLE.outline,
          },
        });
      },
      setEditedStyle: (style) => {
        const { editedShape } = get();
        if (!editedShape) return;
        set({
          editedShape: {
            ...editedShape,
            ...style,
            color:
              editedShape.kind === "point" && style.color
                ? pointColorOf(style.color)
                : (style.color ?? editedShape.color),
          },
        });
      },
      finishEditingShape: (isSaved) => {
        const { editedShape, styles } = get();
        if (!editedShape) return;
        const { markupId, color, outline } = editedShape;
        if (!isSaved) {
          set({ editedShape: undefined });
          return;
        }
        // A point back to the default pin has no style anymore
        const others = omit(styles, [markupId]);
        set({
          editedShape: undefined,
          styles: color
            ? { ...others, [markupId]: { color, outline } }
            : others,
        });
      },
      setCanClosePolygon: (canClosePolygon) => {
        if (canClosePolygon !== get().canClosePolygon) set({ canClosePolygon });
      },
      setWasDiscarded: (wasDiscarded) => set({ wasDiscarded }),
    }),
    {
      // Mocked persistence until the backend can store a style on markups
      name: "sphere-viewer-annotation-shapes-mock",
      // Version 1 resets the style of the next shape to the default one, keeping the styles of the annotations
      version: 1,
      migrate: (persisted) => ({
        ...(isPlainObject(persisted) ? persisted : {}),
        style: DEFAULT_SHAPE_STYLE,
      }),
      partialize: ({ shape, style, pointColor, styles }) => ({
        shape,
        style,
        pointColor,
        styles,
      }),
    },
  ),
);

/** @returns true if annotation shapes and styles are enabled */
export function useIsAnnotationShapesEnabled(): boolean {
  return useAppSelector(selectHasFeature(Features.AnnotationShapes));
}

/**
 * @param markupId id of the markup of the annotation
 * @returns the style of a shape annotation, if it has one
 */
export function useAnnotationShapeStyle(
  markupId: GUID,
): AnnotationShapeStyle | undefined {
  const edited = useAnnotationShapesStore((s) =>
    s.editedShape?.markupId === markupId ? s.editedShape : undefined,
  );
  const saved = useAnnotationShapesStore((s) => s.styles[markupId]);
  if (!edited) return saved;
  return edited.color
    ? { color: edited.color, outline: edited.outline }
    : undefined;
}

/**
 * @param markupId id of the markup of the annotation
 * @returns true if the style of this shape annotation is being edited
 */
export function useIsShapeBeingEdited(markupId: GUID): boolean {
  return useAnnotationShapesStore((s) => s.editedShape?.markupId === markupId);
}

/**
 * @param color color picked for a point
 * @returns the color to keep, undefined for the dark one, which is the default pin without a custom color
 */
function pointColorOf(color: AnnotationColor): AnnotationColor | undefined {
  return color === DARK_ANNOTATION_COLOR ? undefined : color;
}
